import * as React from "react";
import {
  Reseller as ApiReseller,
  useDeleteV4ManufacturerByRouteManufacturerIdResellerAndResellerIdQualificationMutation,
  usePutV4ManufacturerByRouteManufacturerIdResellerAndResellerIdQualificationMutation,
} from "randmar-api-client";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  useToast,
} from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import { useUser } from "../../hooks/useUser";
import LegacyReseller from "../../stores/reseller/Reseller";
import InlineSpinner from "../util/InlineSpinner";

interface IProps {
  reseller: LegacyReseller | ApiReseller;
  children: React.ReactNode;
}

const qualificationOptions = [
  { value: "", label: "No Qualification Status", id: "qual-none", adminOnly: false },
  { value: "Qualified", label: "Qualified", id: "qual-qualified", adminOnly: false },
  { value: "Do Not Sell", label: "Do Not Sell", id: "qual-dns", adminOnly: false },
  { value: "Suspended", label: "Suspended", id: "qual-suspended", adminOnly: true },
];

const ManageQualificationWrapper: React.FC<IProps> = ({ reseller: rawReseller, children }) => {
  const { applicationId } = useManufacturer();
  const [open, setOpen] = React.useState(false);
  const [qualificationId, setQualificationId] = React.useState("");
  const [selectedStatus, setSelectedStatus] = React.useState<string>("");
  const { isSuperAdmin } = useUser();
  const { toast } = useToast();

  const [insertQualification, { isLoading: isInserting }] = usePutV4ManufacturerByRouteManufacturerIdResellerAndResellerIdQualificationMutation();
  const [removeQualification, { isLoading: isRemoving }] = useDeleteV4ManufacturerByRouteManufacturerIdResellerAndResellerIdQualificationMutation();

  const isLegacy = (reseller: LegacyReseller | ApiReseller): reseller is LegacyReseller => "qualifications" in reseller;

  const reseller: ApiReseller = isLegacy(rawReseller)
    ? {
        ResellerId: rawReseller.resellerId,
        Name: rawReseller.name,
        Qualifications:
          rawReseller.qualifications?.map((qualification) => ({
            ManufacturerId: qualification.manufacturerId,
            QualificationId: qualification.qualificationId,
          })) ?? null,
      }
    : rawReseller;

  const resolveCurrentQualification = React.useCallback(() => {
    let currentQualification = (reseller.Qualifications || []).find((q) => q.ManufacturerId === applicationId)?.QualificationId || "";
    if (!isSuperAdmin && currentQualification === "Suspended") {
      currentQualification = "Do Not Sell";
    }
    return currentQualification;
  }, [applicationId, isSuperAdmin, reseller.Qualifications]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setOpen(false);
        return;
      }

      const currentQualification = resolveCurrentQualification();
      if (!currentQualification) {
        setSelectedStatus("");
        setQualificationId("");
      } else if (currentQualification === "Do Not Sell" || currentQualification === "Suspended") {
        setSelectedStatus(currentQualification);
        setQualificationId("");
      } else {
        setSelectedStatus("Qualified");
        setQualificationId(currentQualification);
      }

      setOpen(true);
    },
    [resolveCurrentQualification]
  );

  const handleInsertQualification = React.useCallback(
    async (nextQualificationId: string) => {
    try {
      await insertQualification({
        routeManufacturerId: applicationId,
        resellerId: reseller.ResellerId || "",
        newValue: nextQualificationId || "Qualified",
      }).unwrap();
      toast({ variant: "success", title: "Success", description: `${nextQualificationId || "Qualified"} was successfully saved.` });
      setOpen(false);
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Qualification failed to save. Please try again." });
    }
    },
    [applicationId, insertQualification, reseller.ResellerId, toast]
  );

  const handleRemoveQualification = React.useCallback(async () => {
    try {
      await removeQualification({
        routeManufacturerId: applicationId,
        resellerId: reseller.ResellerId || "",
      }).unwrap();
      toast({ variant: "success", title: "Success", description: "Qualification was successfully removed." });
      setOpen(false);
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Qualification failed to remove. Please try again." });
    }
  }, [applicationId, removeQualification, reseller.ResellerId, toast]);

  const isLoading = isInserting || isRemoving;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>{children}</DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[20px] font-medium text-slate-900">
              Change qualification status for {reseller.Name || "this reseller"}
            </DialogTitle>
          </DialogHeader>
          <RadioGroup
            value={selectedStatus}
            onValueChange={setSelectedStatus}
            className="space-y-1 py-4"
          >
            {qualificationOptions
              .filter((opt) => !opt.adminOnly || isSuperAdmin)
              .map((option) => (
                <div key={option.id} className="space-y-2">
                  <Label
                    htmlFor={option.id}
                    className="flex cursor-pointer items-center gap-3 rounded-xl border border-transparent p-3 transition-colors hover:bg-slate-50"
                  >
                    <RadioGroupItem value={option.value} id={option.id} />
                    <span className="text-[15px] font-medium text-slate-900">{option.label}</span>
                  </Label>
                  {option.value === "Qualified" && selectedStatus === "Qualified" ? (
                    <div className="pl-10">
                      <Label htmlFor="qualificationId" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Qualification ID (optional)
                      </Label>
                      <Input
                        autoFocus
                        id="qualificationId"
                        placeholder="Defaults to Qualified"
                        value={qualificationId}
                        onChange={(e) => setQualificationId(e.target.value)}
                        disabled={isLoading}
                        className="mt-2 h-11 rounded-lg"
                      />
                    </div>
                  ) : null}
                </div>
              ))}
          </RadioGroup>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isLoading} className="rounded-xl">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (selectedStatus === "") {
                  void handleRemoveQualification();
                  return;
                }

                if (selectedStatus === "Qualified") {
                  void handleInsertQualification(qualificationId.trim() || "Qualified");
                  return;
                }

                void handleInsertQualification(selectedStatus);
              }}
              disabled={isLoading}
              className="rounded-xl"
            >
              {isLoading ? <InlineSpinner className="mr-2" decorative /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManageQualificationWrapper;
